import React from "react";
import { Link } from "react-router-dom";
import { ManageCookiesLink } from "../components/CookieConsent";
import { Logo } from "../components/Logo";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import {
  ArrowRight,
  Check,
  Banknote,
  ShieldCheck,
  Ticket,
  Gift,
  Users,
  Sparkles,
} from "lucide-react";

function FeeRow({ label, value, testid }) {
  return (
    <div
      className="flex items-baseline justify-between border-b border-dashed border-[var(--gt-line)] pb-3"
      data-testid={testid}
    >
      <span className="text-sm text-[var(--gt-ink-2)]">{label}</span>
      <span className="font-heading font-black text-base sm:text-lg">{value}</span>
    </div>
  );
}

function Bullet({ children, testid }) {
  return (
    <li className="flex items-start gap-3 text-sm text-[var(--gt-ink-2)]" data-testid={testid}>
      <Check size={16} className="text-[var(--gt-accent)] mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

export default function Pricing() {
  useDocumentMeta({
    title: "Pricing · Australia's lowest ticket booking fees · Getickets",
    description:
      "Getickets is Australia's lowest-fee ticketing platform for organisers. Just 2.5% + $0.30 per ticket. Free events are free. Direct AUD payouts. No lock-in.",
    url:
      typeof window !== "undefined" ? window.location.origin + "/pricing" : undefined,
    type: "website",
  });

  return (
    <div
      className="min-h-screen bg-[var(--gt-bg-marketing)] text-[var(--gt-ink)]"
      data-testid="pricing-page"
    >
      {/* Nav */}
      <nav className="gt-glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Logo size="md" />
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/events"
              data-testid="nav-browse-events-link"
              className="gt-btn-ghost text-sm hidden sm:inline-flex"
            >
              Browse events
            </Link>
            <Link
              to="/pricing"
              data-testid="nav-pricing-link"
              className="gt-btn-ghost text-sm"
              aria-current="page"
            >
              Pricing
            </Link>
            <Link
              to="/who-we-are"
              data-testid="nav-who-we-are-link"
              className="gt-btn-ghost text-sm hidden sm:inline-flex"
            >
              Who We Are
            </Link>
            <Link
              to="/login"
              data-testid="nav-login-btn"
              className="gt-btn-ghost text-sm"
            >
              Log in
            </Link>
            <Link
              to="/signup"
              data-testid="nav-signup-btn"
              className="gt-btn-primary text-sm py-2"
            >
              Sign up
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <div className="gt-label mb-6" data-testid="pricing-eyebrow">
              Transparent · AUD · GST-Ready
            </div>
            <h1
              className="font-heading font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mb-6"
              data-testid="pricing-headline"
            >
              Australia&apos;s lowest ticket
              <br />
              booking fees for organisers
              <span className="text-[var(--gt-accent)]">.</span>
            </h1>
            <p
              className="text-[var(--gt-ink-2)] text-base sm:text-lg leading-relaxed max-w-xl mb-8"
              data-testid="pricing-subhead"
            >
              One simple fee per paid ticket. Free events are free forever. No
              monthly minimums, no lock-in contracts, no surprises at payout time.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/signup"
                className="gt-btn-primary"
                data-testid="pricing-hero-signup-btn"
              >
                Start selling — free to sign up <ArrowRight size={16} />
              </Link>
              <Link
                to="/events"
                className="gt-btn-secondary"
                data-testid="pricing-hero-browse-btn"
              >
                Browse live events
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[var(--gt-ink-3)] tracking-widest uppercase">
              <div className="flex items-center gap-2" data-testid="pricing-hero-badge-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--gt-accent)]" />
                No credit card required
              </div>
              <div className="flex items-center gap-2" data-testid="pricing-hero-badge-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--gt-ink)]" />
                No lock-in
              </div>
              <div className="flex items-center gap-2" data-testid="pricing-hero-badge-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--gt-accent)]" />
                Stripe-secured
              </div>
            </div>
          </div>

          {/* Sticker */}
          <div className="md:col-span-5">
            <div
              className="relative border-2 border-[var(--gt-ink)] rounded-sm bg-white p-8 sm:p-10 shadow-[8px_8px_0px_0px_var(--gt-ink)]"
              data-testid="pricing-headline-card"
            >
              <div className="gt-label mb-4">Per paid ticket</div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span
                  className="font-heading font-black tracking-tighter text-6xl sm:text-7xl leading-none"
                  data-testid="pricing-headline-rate"
                >
                  2.5%
                </span>
                <span className="font-heading font-black text-2xl sm:text-3xl text-[var(--gt-ink-2)]">
                  + $0.30
                </span>
              </div>
              <p className="mt-4 text-sm text-[var(--gt-ink-2)] leading-relaxed">
                Plus 10% GST on the service fee only (never on the ticket price).
                All figures in AUD.
              </p>
              <div className="mt-8 pt-6 border-t border-[var(--gt-line)] space-y-3">
                <FeeRow
                  label="Free events"
                  value="Free forever"
                  testid="pricing-fee-free-events"
                />
                <FeeRow
                  label="Add-ons & donations"
                  value="No booking fee"
                  testid="pricing-fee-addons"
                />
                <FeeRow
                  label="Payment processing"
                  value="Stripe standard"
                  testid="pricing-fee-processing"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who pays the fee */}
      <section
        className="max-w-7xl mx-auto px-6 sm:px-10 pb-16"
        aria-labelledby="who-pays-heading"
        data-testid="pricing-who-pays-section"
      >
        <div className="mb-8">
          <div className="gt-label mb-3">You choose</div>
          <h2
            id="who-pays-heading"
            className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-none"
          >
            Who pays the booking fee?
          </h2>
          <p className="mt-3 text-[var(--gt-ink-2)] max-w-2xl">
            Every event can be configured either way — flip a switch, no penalties.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            className="gt-card border-2 border-[var(--gt-ink)]"
            data-testid="pricing-mode-passed"
          >
            <div className="flex items-start justify-between mb-4">
              <Users size={26} strokeWidth={1.6} className="text-[var(--gt-accent)]" />
              <span className="text-xs font-mono tracking-widest uppercase text-[var(--gt-ink-3)]">
                Default
              </span>
            </div>
            <h3 className="font-heading text-2xl font-bold tracking-tight mb-2">
              Pass fees to the buyer
            </h3>
            <p className="text-sm text-[var(--gt-ink-2)] leading-relaxed mb-5">
              You keep 100% of the ticket price. The 2.5% + $0.30 fee (plus GST)
              is shown transparently on top at checkout. Most Aussie organisers
              use this.
            </p>
            <ul className="space-y-2">
              <Bullet testid="passed-bullet-1">
                You settle at full face value
              </Bullet>
              <Bullet testid="passed-bullet-2">
                Fees itemised for buyers at checkout
              </Bullet>
              <Bullet testid="passed-bullet-3">
                GST invoice generated automatically
              </Bullet>
            </ul>
          </div>

          <div className="gt-card" data-testid="pricing-mode-absorbed">
            <div className="flex items-start justify-between mb-4">
              <Ticket size={26} strokeWidth={1.6} className="text-[var(--gt-accent)]" />
              <span className="text-xs font-mono tracking-widest uppercase text-[var(--gt-ink-3)]">
                Optional
              </span>
            </div>
            <h3 className="font-heading text-2xl font-bold tracking-tight mb-2">
              Absorb the fee yourself
            </h3>
            <p className="text-sm text-[var(--gt-ink-2)] leading-relaxed mb-5">
              Advertise a clean round price (e.g. $50 flat). The 2.5% + $0.30 fee
              plus GST comes out of your settlement — great for premium or gifted
              events.
            </p>
            <ul className="space-y-2">
              <Bullet testid="absorbed-bullet-1">
                Buyers pay exactly the sticker price
              </Bullet>
              <Bullet testid="absorbed-bullet-2">
                Fee deducted before payout
              </Bullet>
              <Bullet testid="absorbed-bullet-3">
                Toggle on/off per event
              </Bullet>
            </ul>
          </div>
        </div>
      </section>

      {/* Everything included */}
      <section
        className="max-w-7xl mx-auto px-6 sm:px-10 pb-16"
        aria-labelledby="included-heading"
        data-testid="pricing-included-section"
      >
        <div className="mb-8">
          <div className="gt-label mb-3">Everything included</div>
          <h2
            id="included-heading"
            className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-none"
          >
            No upsells. No feature paywalls.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            {
              icon: Banknote,
              title: "Direct AUD payouts to your bank",
              body: "Settle straight to your Australian bank account via Stripe. Track pending, paid and reconciled totals in real time.",
              testid: "included-payouts",
            },
            {
              icon: ShieldCheck,
              title: "Signed QR tickets & door scanning",
              body: "Every ticket is a cryptographically signed QR. Scan from any phone browser. No app to install for staff.",
              testid: "included-scanning",
            },
            {
              icon: Sparkles,
              title: "GST-ready reporting",
              body: "BAS-friendly exports, itemised service-fee GST, and per-event revenue breakdowns. All in AUD.",
              testid: "included-reporting",
            },
            {
              icon: Users,
              title: "Team & scanner roles",
              body: "Invite scanners and co-organisers with scoped permissions. Everyone signs in with their own account.",
              testid: "included-team",
            },
            {
              icon: Gift,
              title: "Add-ons & donations — fee-free",
              body: "Sell merch, drinks vouchers, and accept donations at 0% booking fee — you keep every dollar above ticket price.",
              testid: "included-addons",
            },
            {
              icon: Ticket,
              title: "Free events, forever free",
              body: "Community, RSVP-only, and volunteer events pay $0 to use Getickets. Same features, no fees.",
              testid: "included-free",
            },
          ].map(({ icon: Icon, title, body, testid }) => (
            <div key={testid} className="gt-card" data-testid={testid}>
              <Icon size={26} strokeWidth={1.6} className="text-[var(--gt-accent)] mb-4" />
              <h3 className="font-heading text-lg font-bold tracking-tight mb-2">
                {title}
              </h3>
              <p className="text-sm text-[var(--gt-ink-2)] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Example calculation */}
      <section
        className="max-w-7xl mx-auto px-6 sm:px-10 pb-16"
        aria-labelledby="example-heading"
        data-testid="pricing-example-section"
      >
        <div className="mb-8">
          <div className="gt-label mb-3">A quick example</div>
          <h2
            id="example-heading"
            className="font-heading text-3xl sm:text-4xl font-black tracking-tighter leading-none"
          >
            What a $50 ticket really costs.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="gt-card" data-testid="pricing-example-passed">
            <div className="text-xs font-mono tracking-widest uppercase text-[var(--gt-ink-3)] mb-4">
              Fees passed to buyer
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[var(--gt-ink-2)]">Ticket price</span>
                <span className="font-mono">$50.00</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--gt-ink-2)]">Service fee (2.5% + $0.30)</span>
                <span className="font-mono">$1.55</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--gt-ink-2)]">GST on service fee (10%)</span>
                <span className="font-mono">$0.16</span>
              </div>
              <div className="flex justify-between text-sm pt-3 border-t border-[var(--gt-line)]">
                <span className="font-bold">Buyer pays</span>
                <span className="font-heading font-black">$51.71</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-bold text-[var(--gt-accent)]">You settle</span>
                <span className="font-heading font-black text-[var(--gt-accent)]">
                  $50.00
                </span>
              </div>
            </div>
          </div>

          <div className="gt-card" data-testid="pricing-example-absorbed">
            <div className="text-xs font-mono tracking-widest uppercase text-[var(--gt-ink-3)] mb-4">
              Fees absorbed by organiser
            </div>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[var(--gt-ink-2)]">Ticket price</span>
                <span className="font-mono">$50.00</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--gt-ink-2)]">Service fee (2.5% + $0.30)</span>
                <span className="font-mono">−$1.55</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[var(--gt-ink-2)]">GST on service fee (10%)</span>
                <span className="font-mono">−$0.16</span>
              </div>
              <div className="flex justify-between text-sm pt-3 border-t border-[var(--gt-line)]">
                <span className="font-bold">Buyer pays</span>
                <span className="font-heading font-black">$50.00</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-bold text-[var(--gt-accent)]">You settle</span>
                <span className="font-heading font-black text-[var(--gt-accent)]">
                  $48.29
                </span>
              </div>
            </div>
          </div>
        </div>
        <p className="mt-6 text-xs text-[var(--gt-ink-3)] leading-relaxed max-w-3xl">
          Example assumes a single-ticket order in AUD. Payment processing (Stripe)
          fees are charged separately by Stripe at their standard AU rates. GST
          shown applies to the service fee only, never to the ticket face value.
        </p>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        <div
          className="relative overflow-hidden bg-[var(--gt-ink)] text-white rounded-sm p-8 sm:p-12"
          data-testid="pricing-cta-card"
        >
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.25em] uppercase text-white/60 mb-4">
              Ready when you are
            </div>
            <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[1] mb-4">
              Start selling in the next 10 minutes
              <span className="text-[var(--gt-accent)]">.</span>
            </h2>
            <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
              Create your organiser account, publish your first event, and share
              the link. Fees are only ever charged on paid tickets sold.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-[var(--gt-accent)] text-white font-semibold text-sm px-6 py-3 rounded-sm hover:opacity-90 transition-opacity"
                data-testid="pricing-cta-signup-btn"
              >
                Sign up as organiser <ArrowRight size={16} />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 border border-white/40 text-white font-semibold text-sm px-6 py-3 rounded-sm hover:border-white transition-colors"
                data-testid="pricing-cta-login-btn"
              >
                I already have an account
              </Link>
            </div>
            <div
              className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/60 tracking-widest uppercase"
              data-testid="pricing-cta-badges"
            >
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--gt-accent)]" />
                No credit card required
              </div>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-white/60" />
                No lock-in
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[var(--gt-ink)] text-[var(--gt-bg-marketing)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 text-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div
            className="text-[var(--gt-ink-3)] text-xs tracking-widest uppercase"
            data-testid="footer-copyright"
          >
            © {new Date().getFullYear()} Getickets · Made for Aussie organisers.
            Powered by{" "}
            <a
              href="https://getupsolutions.com.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[var(--gt-accent)]"
              data-testid="footer-powered-by-link"
            >
              www.getupsolutions.com.au
            </a>
          </div>
          <div className="flex gap-4 text-xs">
            <Link to="/" className="hover:text-[var(--gt-accent)]" data-testid="footer-home-link">
              Home
            </Link>
            <Link
              to="/events"
              className="hover:text-[var(--gt-accent)]"
              data-testid="footer-events-link"
            >
              Browse events
            </Link>
            <Link
              to="/who-we-are"
              className="hover:text-[var(--gt-accent)]"
              data-testid="footer-who-we-are-link"
            >
              Who we are
            </Link>
            <Link
              to="/privacy"
              className="hover:text-[var(--gt-accent)]"
              data-testid="footer-privacy-link"
            >
              Privacy
            </Link>
            <ManageCookiesLink className="text-xs" />
            <a
              className="hover:text-[var(--gt-accent)]"
              href="mailto:admin@getickets.com.au"
            >
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
